import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { AppUI, ContactEmailState, DeckUI } from '@seedlang/state';
import Spinner from 'components/spinner';
import { isBlank, isValidEmail } from '@seedlang/utils';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import Alert from 'components/alert';
import InPlaceTextInput from 'components/form/in_place_text_input';
import Button from 'components/button/button';
import Text from 'components/text';
import Modal from 'components/modal';
import EmailTypoWarning from 'components/form/email_typo_warning';
import { isPresent } from '@seedlang/utils/src';

const Wrapper = styled.div`
  .spinner > div {
    background-color: ${Theme.blue};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  margin: 10px 0 0 0;
`;

const ContactCategory = styled.div`
  select {
    font-size: 16px;
  }
`;

const TextareaWrapper = styled.div`
  margin-bottom: 10px;
`;

@observer
class ContactForm extends React.Component {
  @observable message = '';
  @observable email = null;
  @observable emailFocused = false;
  @observable submitting = false;
  @observable showMessage = false;
  @observable contactCategoryId;
  emailChange = new ContactEmailState({
    getEmail: () => this.contactEmail,
    setEmail: email => this.email = email,
    canChangeAccountEmail: () => this.canChangeAccountEmail,
  });

  constructor(props) {
    super(props);
    this.selectRef = React.createRef();
    AppUI.contactCategoryStore.getIndex();
  }

  @computed get disabled() {
    if (this.submitting || this.emailChange.saving) return true;
    if (this.showEmailInput && !isValidEmail(this.contactEmail)) return true;
    if (!AppUI.userIsSignedIn) {
      return isBlank(this.message);
    } else if (AppUI.siteIsMemrev && AppUI.contactCategoryStore.indexData?.length > 0) {
      return isBlank(this.message) || !this.contactCategoryId;
    } else {
      return isBlank(this.message);
    }
  }

  @computed get canChangeAccountEmail() {
    return AppUI.siteIsSeedlang && AppUI.userIsSignedInAndNotGuest && !AppUI.user.confirmed;
  }

  @computed get showEmailInput() {
    return !AppUI.userIsSignedIn || this.canChangeAccountEmail;
  }

  @computed get contactEmail() {
    return this.email ?? (this.canChangeAccountEmail ? AppUI.user.email : '');
  }

  onEmailChange = value => this.emailChange.changeEmail(value);

  onEmailBlur = () => { this.emailFocused = false; };

  onSubmit = () => {
    if (this.disabled) return;
    if (this.showEmailInput) this.emailChange.submit(this.sendMessage);
    else this.sendMessage();
  };

  sendMessage = () => {
    if (this.disabled) return;
    this.submitting = true;
    AppUI.contactMessageStore.create({
      data: {
        siteId: AppUI.user?.site?.id,
        message: this.message,
        debug_info: DeckUI.contactDebugInfo({
          appVersion: AppUI.user?.site?.currentWebVersion,
        }),
        email: this.showEmailInput ? this.contactEmail.trim() : this.email,
        webVersion: AppUI.user?.site?.currentWebVersion,
        contactCategoryId: this.contactCategoryId === 'other' ? AppUI.user?.site?.supportEmailAddress : this.contactCategoryId,
      },
    }, this.afterSubmitMessage);
  }
  afterSubmitMessage = () => {
    this.message = '';
    this.email = null;
    this.contactCategoryId = null;
    if (isPresent(this.selectRef.current)) {
      this.selectRef.current.value = '';
    }
    this.submitting = false;
    this.showMessage = true;
  }
  onReset = () => {
    this.message = '';
    this.email = null;
    this.emailChange.decidedEmail = null;
    this.emailChange.sentTo = null;
    this.emailChange.error = null;
    this.emailChange.showModal = false;
    this.emailChange.pendingSubmit = null;
    this.contactCategoryId = null;
    if (isPresent(this.selectRef.current)) {
      this.selectRef.current.value = '';
    }
    this.submitting = false;
    this.showMessage = false;
  }
  onSelectContactCategoryId = () => {
    this.contactCategoryId = this.selectRef.current.value;
  }

  @computed get selectedContactCategory() {
    return AppUI.contactCategoryStore.hasIndexData && AppUI.contactCategoryStore.indexData.find(item => item.id === this.contactCategoryId);
  }

  render() {
    return (
      <Wrapper>
        {this.emailChange.showModal &&
          <Modal
            title='Change Your Account Email?'
            background={Theme.blue}
            width='100%'
            maxWidth='560px'
            onCloseModal={this.emailChange.cancel}
          >
            <p>Would you also like to change your Seedlang account’s email address from <strong>{AppUI.user.email}</strong> to <strong>{this.contactEmail.trim().toLowerCase()}</strong>?</p>
            <EmailTypoWarning email={this.contactEmail} />
            {this.emailChange.error && <Alert background={Theme.red} role='alert'>{this.emailChange.error}</Alert>}
            <ButtonRow style={{ justifyContent: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <Button background={Theme.blue} disabled={this.emailChange.saving} onClick={this.emailChange.save}>
                {this.emailChange.saving ? 'Updating…' : 'Update Account Email'}
              </Button>
              <Button background={Theme.gray} disabled={this.emailChange.saving} onClick={this.emailChange.finish}>Use for This Message Only</Button>
            </ButtonRow>
          </Modal>
        }
        {this.emailChange.sentTo &&
          <Alert background={Theme.green} role='status'>
            Your account email has been updated. We sent a confirmation email to {this.emailChange.sentTo}.
          </Alert>
        }
        {
          AppUI.contactMessageStore.hasOutstandingRequests &&
          <Spinner />
        }
        {
          this.showMessage &&
          <Alert
            textAlign='center'
            margin='20px 0 0 0'
            background={Theme.green}
          >
            Your message was successfully sent.
          </Alert>
        }
        {
          this.showEmailInput &&
          <Text
            padding='10px 0'
            textAlign='left'
            bold
          >
            Email
          </Text>
        }
        {
          this.showEmailInput &&
          <InPlaceTextInput
            type='email'
            value={this.contactEmail}
            onChange={this.onEmailChange}
            onBlur={this.onEmailBlur}
            onFocus={() => this.emailFocused = true}
          />
        }
        {this.showEmailInput && <EmailTypoWarning email={this.contactEmail} focused={this.emailFocused} />}
        {this.showEmailInput && !this.emailFocused && this.contactEmail && !isValidEmail(this.contactEmail) &&
          <Alert background={Theme.red} role='alert'>Please enter a valid email address.</Alert>
        }
        {
          AppUI.siteIsMemrev && AppUI.contactCategoryStore.indexData?.length > 0 &&
          <ContactCategory>
            <Text
              padding='10px 0'
              textAlign='left'
              bold
            >
              What can we help you with?
            </Text>
            <select
              ref={this.selectRef}
              onChange={this.onSelectContactCategoryId}
              defaultValue=''
            >
              <option value='' disabled selected>Select a category</option>
              {
                AppUI.contactCategoryStore.indexData.map(item =>
                  <option value={item.id} key={item.id}>{item.name}</option>,
                )
              }
              <option value='other'>Other</option>
            </select>
          </ContactCategory>
        }
        {
          AppUI.siteIsMemrev && this.selectedContactCategory?.email?.endsWith('seedlang.com') &&
          <Alert margin='10px 0 0 0' background={AppUI.site.accentColor}>This message will be sent to the team of our learning platform, Seedlang.</Alert>
        }
        <Text
          padding='10px 0'
          textAlign='left'
          bold
        >
          Message
        </Text>
        <TextareaWrapper> 
          <InPlaceTextInput
            value={this.message}
            inputType='textarea'
            onChange={val => this.message = val}
            height='140px'
            expandedHeight='140px'
          />
        </TextareaWrapper>
        <ButtonRow>
          <Button
            background={Theme.green}
            color='#FFF'
            width={130}
            disabled={this.disabled}
            onClick={this.onSubmit}
            margin='0 10px 0 0'
          >
            Send
          </Button>
          <Button
            background={Theme.red}
            color='#FFF'
            text='Reset'
            width={130}
            marginLeft={10}
            disabled={this.disabled}
            onClick={this.onReset}
          >
            Reset
          </Button>
        </ButtonRow>
      </Wrapper>
    );
  }
}

export default ContactForm;
